import { el } from 'redom';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import application from 'application';
import serverInterface from 'server-interface';

import TextInputRow from 'generics/components/text-input-row';

import {
  findIndexByPropertyValue
} from 'utilities/collection';

import HelptapEditor from '../../../../helpers';

import FilterSelector from '../../data-collection/filter-selector';

import RowsInputPanel from './row-input-panel';
import FieldsSelector from './field-selector';

import './index.scss';

class AdvancedDataCollectionChildrenSettings {

  constructor(configuration) {

    this.editor = configuration.editor;
    this.node = configuration.node;
    this.activeNodePath = configuration.activeNodePath;
    this.parentEntityId = configuration.parentEntityId;

    this.filtersManager = application.dataManagers.get('DataCollectionFiltersManager');
    this.fieldsSelector = new FieldsSelector({
      ...configuration,
      onSelect: this.handleFieldSelection.bind(this),
      onDeselect: this.handleFieldUnselection.bind(this)
    });
    this.rowsInputPanel = new RowsInputPanel(configuration);
    this.columnsInputRow = new TextInputRow({
      labelClass: 'side-value-control-label field-label sub-text',
      label: 'Columns to use',
      type: 'number',
      attributes: {
        min: 1,
        max: 6
      },
      value: this.node.properties.columnsCount,
      onChange: this.handleColumnsCountChange.bind(this)
    });
    this.filterSelector = new FilterSelector({
      application,
      pageId: this.parentEntityId,
      nodeId: this.node.properties.id,
      collectionId: this.node.properties.collectionId,
      filterId: this.node.properties.filterId,
      onSelect: this.handleFilterSelection.bind(this)
    });

    this.el = el('#advanced-data-collection-children-settings-main', [
      this.fieldsSelector,
      this.rowsInputPanel,
      this.columnsInputRow,
      this.filterSelector
    ]);

  }

  async updateReference(filterId) {

    const pageId = this.parentEntityId;
    const nodeId = this.node.properties.id;
    const collectionId = this.node.properties.collectionId;
    const previousFilterId = this.node.properties.filterId;

    if (previousFilterId === filterId) {
      return;
    }

    await Promise.all([
      this.filtersManager.pushFilterReference(collectionId, filterId, {
        type: 'PAGE',
        _id: `${pageId}:${nodeId}`
      }),
      this.filtersManager.pullFilterReference(collectionId, previousFilterId, {
        type: 'PAGE',
        _id: `${pageId}:${nodeId}`
      })
    ]);

  }

  async tryUpdateManagerFilter(filterId) {

    const projection = ['managerRoot', 'managerId'];
    const page = await serverInterface.startup.pages.getPopulatedPageById(this.parentEntityId, projection);

    if (page.managetRoot !== true) {
      return;
    }

    await serverInterface.startup.pagesManagers.updatePagesManagerFilterById(page.managerId, {
      filterId
    });

  }

  async handleFilterSelection(filterId) {

    await this.updateReference(filterId);

    this.tryUpdateManagerFilter(filterId);

    const [collection, records] = await Promise.all([
      application.serverInterface.startup.dataCollections.getPopulatedCollectionById(this.node.properties.collectionId),
      application.serverInterface.startup.dataCollections.getRecordsByFilter(this.node.properties.collectionId, filterId, {
        skip: 0,
        limit: this.node.properties.rowsCount
      })
    ]);

    const node = basicEditorValueSerializer.utilities.dataCollections.refreshDataCollectionNode(collection, records, this.node);

    if (node.children.length > 10) {
      node.children.length = 10;
    }

    HelptapEditor.setAttributes(this.editor, { properties: { ...this.node.properties, filterId } }, this.activeNodePath);
    HelptapEditor.replaceChildren(this.editor, this.activeNodePath, node.children, this.node.children.length);

  }

  handleColumnsCountChange(event, columnsCount) {

    if (event.target.validity.valid === false) {
      return;
    }

    HelptapEditor.setAttributes(this.editor, {
      properties: {
        ...this.node.properties,
        columnsCount: +columnsCount
      }
    }, this.activeNodePath);

  }

  async handleFieldSelection(columnIds) {

    const columns = structuredClone(this.node.properties.columns);

    for (let columnId of columnIds) {

      const index = findIndexByPropertyValue(columns, '_id', columnId);

      columns[index].displayed = true;

    }

    const records = await this.getPreviewRecords();

    HelptapEditor.dataCollection.updateColumns(this.editor, this.node, columns, this.activeNodePath, records);

  }

  async handleFieldUnselection(columnIds, checkbox) {

    const columns = structuredClone(this.node.properties.columns);

    for (let columnId of columnIds) {
      const index = findIndexByPropertyValue(columns, '_id', columnId);

      columns[index].displayed = false;
    }

    const records = await this.getPreviewRecords();

    HelptapEditor.dataCollection.updateColumns(this.editor, this.node, columns, this.activeNodePath, records);

  }

  getPreviewRecords() {

    return application.serverInterface.startup.dataCollections.getRecordsByFilter(this.node.properties.collectionId, this.node.properties.filterId, {
      skip: 0,
      limit: this.node.properties.rowsCount
    });

  }

  update(configuration) {

    this.editor = configuration.editor;
    this.node = configuration.node;
    this.activeNodePath = configuration.activeNodePath;
    this.parentEntityId = configuration.parentEntityId;

    this.fieldsSelector.update(configuration);
    this.rowsInputPanel.update(configuration);
    this.filterSelector.update({
      application,
      pageId: this.parentEntityId,
      nodeId: this.node.properties.id,
      collectionId: this.node.properties.collectionId,
      filterId: this.node.properties.filterId,
      onSelect: this.handleFilterSelection.bind(this)
    });

  }

}

export default AdvancedDataCollectionChildrenSettings;